import classNames from 'classnames';

import { TDetailedEnabledSurvey } from '@lootboy/lib/slices/surveys/types';
import { getMaxScale } from '@lootboy/lib/slices/surveys/utils';

import { Divider } from '~/components/atoms/Divider';
import { ListContainer } from '~/components/atoms/ListContainer';
import Markdown from '~/components/Markdown';

import { useDocumentTitle } from '~/hooks/useDocumentTitle';
import { SurveyHeroImage } from '~/views/SurveyView/FetchedSurveyContent/SurveyHeroImage';
import { SurveyTimer } from '~/views/SurveyView/FetchedSurveyContent/SurveyTimer';
import { VoteItem } from '~/views/SurveyView/FetchedSurveyContent/VoteItem';

import markdownStyles from '~/sass/typography.module.scss';

import styles from './FetchedSurveyContent.module.scss';

export function FetchedSurveyContent({
  survey,
}: {
  survey: TDetailedEnabledSurvey;
}): JSX.Element {
  useDocumentTitle(survey.headline || survey.name);

  const maxScale = survey.votingResults
    ? getMaxScale(survey.votingResults)
    : 100;

  const isExpired =
    !!survey.availableUntil &&
    new Date(survey.availableUntil).valueOf() < Date.now();

  const isAvailable =
    !survey.availableFrom ||
    new Date(survey.availableFrom).valueOf() < Date.now();

  const shouldShowAfterEndContent = !!survey.afterEndContent && isExpired;

  return (
    <main>
      <SurveyHeroImage image={survey.logo} />
      <section
        className={classNames(markdownStyles.withTypography, styles.content)}
      >
        {survey.headline && (
          <h1 className={styles.headline}>{survey.headline}</h1>
        )}
        <Markdown
          className={classNames(markdownStyles.withTypography, styles.markdown)}
        >
          {survey.content && !shouldShowAfterEndContent
            ? survey.content
            : survey.afterEndContent}
        </Markdown>
        <SurveyTimer
          availableFrom={survey.availableFrom}
          availableUntil={survey.availableUntil}
          isExpired={isExpired}
          isAvailable={isAvailable}
        />
        <Divider className={styles.divider} />
        <ListContainer>
          {(survey.votingResults || survey.votingOptions).map((option) => (
            <VoteItem
              key={option._id ?? option.label}
              maxScale={maxScale}
              voteItem={option}
              surveyId={survey._id}
              disabled={!isAvailable || isExpired}
            />
          ))}
        </ListContainer>
        <Divider className={styles.divider} />
        {survey.terms && (
          <Markdown
            className={classNames(markdownStyles.withTypography, styles.terms)}
          >
            {survey.terms}
          </Markdown>
        )}
      </section>
    </main>
  );
}
